import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, ArrowRight, ArrowLeft, Upload, Loader2 } from "lucide-react";
import { Layout } from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";

/* --- helpers --------------------------------------------------------- */
const HOURS = ["09:30", "11:00", "13:00", "15:00", "17:00"];

function addDays(d, n) { const c = new Date(d); c.setDate(c.getDate() + n); return c; }
function ymd(d) { return d.toISOString().slice(0, 10); }
function humanDate(d) {
  return new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short" }).format(d);
}

/* --- step tracker --------------------------------------------------- */
function Steps({ step }) {
  const items = ["Engagement", "Time", "Details", "Requested"];
  return (
    <ol className="flex flex-wrap gap-3 mb-10">
      {items.map((label, i) => {
        const active = i === step;
        const done = i < step;
        return (
          <li
            key={label}
            className={`flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] ${
              active ? "text-charcoal" : done ? "text-gold" : "text-slate2/40"
            }`}
          >
            <span
              className={`w-6 h-6 border flex items-center justify-center ${
                active ? "border-charcoal bg-charcoal text-ivory" : done ? "border-gold text-gold" : "border-slate2/30"
              }`}
            >
              {done ? <Check size={12} /> : i + 1}
            </span>
            {label}
          </li>
        );
      })}
    </ol>
  );
}

/* --- page ------------------------------------------------------------ */
export default function Booking() {
  const [params, setParams] = useSearchParams();
  const initialSlug = params.get("service") || "";

  const [services, setServices] = useState([]);
  const [step, setStep] = useState(0);
  const [serviceSlug, setServiceSlug] = useState(initialSlug);

  const [slotDate, setSlotDate] = useState(ymd(addDays(new Date(), 3)));
  const [slotTime, setSlotTime] = useState("");

  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "", notes: "" });
  const [cvFile, setCvFile] = useState(null);
  const [cvFileId, setCvFileId] = useState(null);
  const [cvUploading, setCvUploading] = useState(false);

  const [confirmed, setConfirmed] = useState(null);
  const [busy, setBusy] = useState(false);

  const service = useMemo(() => services.find((s) => s.slug === serviceSlug), [services, serviceSlug]);

  useEffect(() => {
    api.get("/services", { params: { category: "career_advisory" } }).then((r) => {
      setServices(r.data);
      if (!initialSlug && r.data[0]) setServiceSlug(r.data[0].slug);
    });
  }, [initialSlug]);

  useEffect(() => {
    if (serviceSlug) setParams({ service: serviceSlug }, { replace: true });
  }, [serviceSlug, setParams]);

  const nextDays = useMemo(() => Array.from({ length: 10 }, (_, i) => addDays(new Date(), i + 2)), []);

  /* --- actions --- */
  const uploadCv = async (file) => {
    if (!file) return;
    setCvUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/bookings/upload-cv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCvFileId(data.file_id);
      toast.success("CV uploaded.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCvUploading(false);
    }
  };

  const submitRequest = async () => {
    if (!service || !slotTime) return;
    setBusy(true);
    try {
      const slotIso = new Date(`${slotDate}T${slotTime}:00`).toISOString();
      const { data } = await api.post("/bookings", {
        service_id: service.id,
        service_slug: service.slug,
        slot_iso: slotIso,
        client_name: form.name,
        client_email: form.email,
        client_phone: form.phone,
        client_role: form.role,
        notes: form.notes,
        cv_file_id: cvFileId,
      });
      setConfirmed(data);
      setStep(3);
      toast.success("Request received.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const canNextStep0 = !!service;
  const canNextStep1 = !!slotDate && !!slotTime;
  const canSubmit = form.name && form.email && form.phone;

  return (
    <Layout>
      <section className="pt-32 pb-8 lg:pt-40" data-testid="booking-page">
        <div className="container-editorial">
          <div className="eyebrow">Request an appointment</div>
          <h1 className="font-display text-4xl md:text-6xl mt-6 leading-[1.05] tracking-tight max-w-3xl">
            A <span className="italic text-gold">private</span> conversation. Requested on your terms.
          </h1>
          <p className="mt-6 max-w-2xl text-slate2 leading-relaxed">
            Choose an engagement and a preferred time. Roy will personally reply within one business day to confirm
            the meeting and — where fees apply — arrange them privately, outside the site.
          </p>
        </div>
      </section>

      <section className="py-8 pb-24">
        <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* SUMMARY */}
          <aside className="lg:col-span-4 order-2 lg:order-1">
            <div className="panel sticky top-28" data-testid="booking-summary">
              <div className="eyebrow">Request summary</div>
              <div className="font-display text-2xl mt-4 leading-tight">{service?.title || "—"}</div>
              {service?.tagline && <div className="italic text-gold mt-1">{service.tagline}</div>}

              {service?.duration_minutes && (
                <div className="hairline mt-6 pt-6 flex items-center justify-between">
                  <span className="text-slate2 text-sm">Duration</span>
                  <span className="font-display text-lg">{service.duration_minutes} min</span>
                </div>
              )}

              {slotTime && (
                <div className="hairline mt-6 pt-6">
                  <div className="eyebrow">Preferred slot</div>
                  <div className="mt-2 font-display text-lg">
                    {humanDate(new Date(slotDate))} · {slotTime}
                  </div>
                </div>
              )}

              <div className="hairline mt-6 pt-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold">Fees</div>
                <p className="text-xs text-slate2 mt-2 leading-relaxed">
                  Fees, where applicable, are shared privately with the client after the request is received.
                  No payment is taken through this site.
                </p>
              </div>
            </div>
          </aside>

          {/* STEPS */}
          <div className="lg:col-span-8 order-1 lg:order-2">
            <Steps step={step} />

            {/* STEP 0 */}
            {step === 0 && (
              <div data-testid="booking-step-service">
                <div className="eyebrow">01 · Choose your engagement</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
                  {services.map((s) => {
                    const active = s.slug === serviceSlug;
                    return (
                      <button
                        key={s.slug}
                        onClick={() => setServiceSlug(s.slug)}
                        className={`text-left p-6 border transition-colors ${
                          active ? "border-gold bg-white" : "border-[#E0DCD3] bg-white hover:border-charcoal"
                        }`}
                        data-testid={`booking-service-${s.slug}`}
                      >
                        <div className="font-display text-xl leading-tight">{s.title}</div>
                        <div className="mt-2 text-sm text-slate2">{s.tagline}</div>
                        {s.duration_minutes && (
                          <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-slate2">
                            {s.duration_minutes} min
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-10 flex justify-end">
                  <button
                    disabled={!canNextStep0}
                    onClick={() => setStep(1)}
                    className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="booking-next-1"
                  >
                    Next · Choose a time <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 1 */}
            {step === 1 && (
              <div data-testid="booking-step-time">
                <div className="eyebrow">02 · Choose your time</div>
                <div className="mt-6">
                  <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate2 mb-2">Day</div>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    {nextDays.map((d) => {
                      const key = ymd(d);
                      const active = key === slotDate;
                      return (
                        <button
                          key={key}
                          onClick={() => { setSlotDate(key); setSlotTime(""); }}
                          className={`p-3 border font-mono text-xs ${
                            active ? "border-gold bg-white text-charcoal" : "border-[#E0DCD3] bg-white text-slate2 hover:border-charcoal"
                          }`}
                          data-testid={`booking-day-${key}`}
                        >
                          <div className="uppercase tracking-widest text-[9px]">{humanDate(d).split(" ")[0]}</div>
                          <div className="font-display text-lg mt-1">{d.getDate()}</div>
                          <div className="uppercase tracking-widest text-[9px]">
                            {new Intl.DateTimeFormat("en-GB", { month: "short" }).format(d)}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-8">
                  <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate2 mb-2">Time (local)</div>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {HOURS.map((t) => {
                      const active = t === slotTime;
                      return (
                        <button
                          key={t}
                          onClick={() => setSlotTime(t)}
                          className={`p-3 border font-mono text-sm ${
                            active ? "border-gold bg-white text-charcoal" : "border-[#E0DCD3] bg-white text-slate2 hover:border-charcoal"
                          }`}
                          data-testid={`booking-time-${t}`}
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-10 flex justify-between">
                  <button onClick={() => setStep(0)} className="btn-outline" data-testid="booking-back-0">
                    <ArrowLeft size={14} /> Back
                  </button>
                  <button
                    disabled={!canNextStep1}
                    onClick={() => setStep(2)}
                    className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="booking-next-2"
                  >
                    Next · Your details <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2 */}
            {step === 2 && (
              <div data-testid="booking-step-details">
                <div className="eyebrow">03 · Your details</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                  <div>
                    <label className="eyebrow eyebrow-slate">Full name</label>
                    <input className="input-flush mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-input-name" />
                  </div>
                  <div>
                    <label className="eyebrow eyebrow-slate">Email</label>
                    <input type="email" className="input-flush mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-input-email" />
                  </div>
                  <div>
                    <label className="eyebrow eyebrow-slate">Phone</label>
                    <input className="input-flush mt-1" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="booking-input-phone" />
                  </div>
                  <div>
                    <label className="eyebrow eyebrow-slate">Current role / level</label>
                    <input className="input-flush mt-1" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="booking-input-role" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="eyebrow eyebrow-slate">Notes for Roy (optional)</label>
                    <textarea rows={3} className="input-flush mt-1 resize-none" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="booking-input-notes" />
                  </div>

                  <div className="md:col-span-2">
                    <label className="eyebrow eyebrow-slate">Résumé / CV (optional · PDF, DOC, DOCX)</label>
                    <label className="dropzone mt-3 flex flex-col items-center gap-2" data-testid="booking-cv-dropzone">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.txt,.rtf"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0] || null;
                          setCvFile(f);
                          if (f) uploadCv(f);
                        }}
                        data-testid="booking-cv-input"
                      />
                      {cvUploading ? (
                        <><Loader2 className="animate-spin" size={16} /><span className="text-sm">Uploading…</span></>
                      ) : cvFileId ? (
                        <><Check size={16} className="text-gold" /><span className="text-sm">{cvFile?.name}</span></>
                      ) : (
                        <><Upload size={16} /><span className="text-sm">Drop your CV or click to select</span></>
                      )}
                    </label>
                  </div>
                </div>

                <div className="mt-10 flex justify-between">
                  <button onClick={() => setStep(1)} className="btn-outline" data-testid="booking-back-1">
                    <ArrowLeft size={14} /> Back
                  </button>
                  <button
                    disabled={!canSubmit || busy}
                    onClick={submitRequest}
                    className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="booking-submit"
                  >
                    {busy ? "…" : "Send request"} <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 3 — CONFIRMED */}
            {step === 3 && confirmed && (
              <div data-testid="booking-step-confirmed">
                <div className="eyebrow">Received</div>
                <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight tracking-tight">
                  Thank you, {confirmed.client_name.split(" ")[0]}. Your request is <span className="italic text-gold">with Roy</span>.
                </h2>
                <div className="panel mt-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className="eyebrow">Engagement</div>
                      <div className="font-display text-2xl mt-2">{confirmed.service_title}</div>
                    </div>
                    <div>
                      <div className="eyebrow">Preferred slot</div>
                      <div className="font-display text-2xl mt-2">
                        {new Date(confirmed.slot_iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                      </div>
                    </div>
                    <div>
                      <div className="eyebrow">Reference</div>
                      <div className="font-mono text-sm mt-2 break-all" data-testid="booking-confirmation-id">{confirmed.id}</div>
                    </div>
                    <div>
                      <div className="eyebrow">Contact</div>
                      <div className="font-display text-lg mt-2 break-all">{confirmed.client_email}</div>
                    </div>
                  </div>
                </div>
                <p className="mt-8 text-slate2 max-w-xl leading-relaxed">
                  A confirmation has been dispatched to <strong>{confirmed.client_email}</strong>. Roy will personally
                  reply within one business day to confirm the meeting and, where fees apply, arrange them privately.
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <Link to="/" className="btn-primary">Return home</Link>
                  <Link to="/services" className="btn-outline">Explore the practice</Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
